import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

export interface Notification {
  id: string;
  user_id: string;
  vineyard_id?: string;
  type: 'alert' | 'info' | 'warning' | 'success' | 'task' | 'compliance' | 'equipment' | 'system';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  is_read: boolean;
  read_at?: string;
  action_url?: string;
  action_label?: string;
  metadata?: Record<string, any>;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

interface NotificationPreferences {
  user_id: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  notification_types?: Record<string, any>;
}

export const useNotifications = () => {
  const queryClient = useQueryClient();
  const [pollingInterval, setPollingInterval] = useState<number | false>(30000); // 30 seconds

  // Fetch notifications
  const { data: notificationsData, isLoading, error } = useQuery<{ notifications: Notification[]; total: number }>(
    ['notifications'],
    async () => {
      const response = await api.get('/api/notifications', {
        params: {
          is_read: false, // Only get unread by default
          limit: 50
        }
      });
      return response.data;
    },
    {
      refetchInterval: pollingInterval,
      staleTime: 10000, // 10 seconds
    }
  );

  // Fetch unread count
  const { data: unreadCountData } = useQuery<{ unread_count: number }>(
    ['notifications', 'unread-count'],
    async () => {
      const response = await api.get('/api/notifications/unread-count');
      return response.data;
    },
    {
      refetchInterval: pollingInterval,
      staleTime: 5000, // 5 seconds
    }
  );

  // Fetch all notifications (for notification center)
  const { data: allNotifications, refetch: refetchAll } = useQuery<{ notifications: Notification[]; total: number }>(
    ['notifications', 'all'],
    async () => {
      const response = await api.get('/api/notifications', {
        params: {
          limit: 100,
          offset: 0
        }
      });
      return response.data;
    },
    {
      enabled: false, // Only fetch when explicitly called
    }
  );

  // Mark notification as read
  const markAsReadMutation = useMutation(
    async (notificationId: string) => {
      const response = await api.post(`/api/notifications/${notificationId}/read`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notifications', 'unread-count']);
      },
    }
  );

  // Mark all as read
  const markAllAsReadMutation = useMutation(
    async () => {
      const response = await api.post('/api/notifications/mark-all-read');
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notifications', 'unread-count']);
        queryClient.invalidateQueries(['notifications', 'all']);
      },
    }
  );

  // Delete notification
  const deleteNotificationMutation = useMutation(
    async (notificationId: string) => {
      const response = await api.delete(`/api/notifications/${notificationId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications']);
        queryClient.invalidateQueries(['notifications', 'unread-count']);
        queryClient.invalidateQueries(['notifications', 'all']);
      },
    }
  );

  // Fetch notification preferences
  const { data: preferences } = useQuery<NotificationPreferences>(
    ['notifications', 'preferences'],
    async () => {
      const response = await api.get('/api/notifications/preferences');
      return response.data;
    }
  );

  // Update preferences
  const updatePreferencesMutation = useMutation(
    async (newPreferences: Partial<NotificationPreferences>) => {
      const response = await api.put('/api/notifications/preferences', newPreferences);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications', 'preferences']);
      },
    }
  );

  const markAsRead = useCallback((notificationId: string) => {
    markAsReadMutation.mutate(notificationId);
  }, [markAsReadMutation]);

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const deleteNotification = useCallback((notificationId: string) => {
    deleteNotificationMutation.mutate(notificationId);
  }, [deleteNotificationMutation]);

  const updatePreferences = useCallback((newPreferences: Partial<NotificationPreferences>) => {
    updatePreferencesMutation.mutate(newPreferences);
  }, [updatePreferencesMutation]);

  // Enable/disable polling
  const enablePolling = useCallback(() => {
    setPollingInterval(30000);
  }, []);

  const disablePolling = useCallback(() => {
    setPollingInterval(false);
  }, []);

  return {
    notifications: notificationsData?.notifications || [],
    allNotifications: allNotifications?.notifications || [],
    unreadCount: unreadCountData?.unread_count || 0,
    preferences,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updatePreferences,
    refetchAll,
    enablePolling,
    disablePolling,
  };
};

